import { isNull, isNumber } from 'lodash-es';
import { asyncAttempt } from '@namespace/helpers';
import { deposit, withdraw } from '../api/balance';
import { DepositParams, WithdrawParams } from '../api/balance/types';
import { getRecommendedDepositAmount } from '../api/getRecommendedDepositAmount';
import { getBalance as getMoneyboxBalance } from '../api/moneybox';
import { addToken, getTokens, updateToken } from '../api/tokens';
import {
  IMPSPaymentTokenObject,
  PaymentTokenObject,
  RixusPaymentTokenObject
} from '../api/tokens/types';
import { PaymentType, ResultDepositData } from '../types/common';
import { CryptoLimits } from '../types/cryptoLimits';
import { Network } from '../types/network';
import { NormalizedPaymentServices } from '../utils/normalizePaymentServices';
import { STORE_PROPS } from './constants';
import { usePaymentsStore } from './store';

interface ActionsCreatorParams {
  setState: typeof usePaymentsStore.setState;
  getState: typeof usePaymentsStore.getState;
}

export const actionsCreator = ({
  setState,
  getState
}: ActionsCreatorParams) => ({
  GET_SW_CONFIG: (paymentServices: NormalizedPaymentServices) => {
    setState({
      [STORE_PROPS.PAYMENT_SERVICES]: paymentServices,
      [STORE_PROPS.IS_PAYMENT_SERVICES_LOADED]: true
    });
  },
  GET_USER_TOKENS: async (serviceId: number, paymentType: PaymentType) => {
    const requestParams = { service_id: serviceId };
    const isForWithdraw = paymentType === 'withdraw';

    const withdrawRequestParams = {
      ...requestParams,
      only_for_withdraw: isForWithdraw,
      statuses: isForWithdraw
    };
    const tokens = await getTokens(
      (isForWithdraw && withdrawRequestParams) || requestParams
    );

    const { activePaymentType } = getState();
    const isPaymentTypeChanged = paymentType !== activePaymentType;

    if (isPaymentTypeChanged) {
      return;
    }

    const response = tokens?.response || [];
    const userTokens = response.map(({ statuses = {}, ...itemProps }) => ({
      hasNtf: statuses.docRequested,
      ...itemProps
    }));

    setState({
      [STORE_PROPS.USER_TOKENS]: userTokens
    });
  },
  UPDATE_USER_TOKEN: async (
    serviceId: number,
    paymentToken: string,
    tokenObj: PaymentTokenObject
  ) => {
    const res = await updateToken({
      serviceId,
      data: { paymentToken, paymentTokenObject: tokenObj }
    });
    return res?.response;
  },
  ADD_USER_TOKEN: async (
    serviceId: number,
    token: string | RixusPaymentTokenObject | IMPSPaymentTokenObject,
    tokenType: string
  ) => {
    const data =
      tokenType === 'custom' && isNumber(token)
        ? { paymentTokenObject: { accountId: token } }
        : { [tokenType]: token };

    const res = await addToken({
      serviceId,
      data
    });

    // TODO: Consider updating the token value in the store according to the API
    //  response in order to avoid an additional request to the API service for updating the token list.
    return res?.response;
  },
  DEPOSIT: async (params: DepositParams) => {
    const resultDepositData = await deposit(params);

    setState({ [STORE_PROPS.RESULT_DEPOSIT_DATA]: resultDepositData });

    return resultDepositData;
  },
  WITHDRAW: async (params: WithdrawParams) => {
    const resultWithdrawData = await withdraw(params);

    setState({ [STORE_PROPS.RESULT_DEPOSIT_DATA]: resultWithdrawData });

    return resultWithdrawData;
  },
  SET_ACTIVE_SERVICE: (activeServiceId: number) => {
    setState({ [STORE_PROPS.ACTIVE_SERVICE_ID]: activeServiceId });
  },
  SET_ACTIVE_PAYMENT_TYPE: (activePaymentType: PaymentType) => {
    setState({ [STORE_PROPS.ACTIVE_PAYMENT_TYPE]: activePaymentType });
  },
  GET_RECOMMENDED_DEPOSIT_AMOUNT: async () => {
    const [error, payload] = await asyncAttempt(getRecommendedDepositAmount);

    if (!error && payload) {
      const { customData = {} } = payload;
      setState({
        [STORE_PROPS.RECOMMENDED_DEPOSIT_AMOUNT]: customData.depositAmount
      });
    }
  },
  SET_DEPOSIT_RESULT: (resultDepositData: ResultDepositData) => {
    setState({ [STORE_PROPS.RESULT_DEPOSIT_DATA]: resultDepositData });
  },
  GET_MONEYBOX_BALANCE: async () => {
    const [error, payload] = await asyncAttempt(getMoneyboxBalance);

    if (!error && payload) {
      setState({ [STORE_PROPS.MONEYBOX_BALANCE]: payload });
    }
  },
  SET_ACTIVE_NETWORK: ({
    activeNetwork,
    networks = []
  }: {
    activeNetwork: string;
    networks: Network[];
  }) => {
    setState((state) => {
      const { paymentServices, activePaymentType, activeServiceId } = state;

      if (isNull(activePaymentType) || isNull(activeServiceId)) {
        return state;
      }

      const currentNetwork = networks.find(
        ({ network }) => network === activeNetwork
      );
      return {
        [STORE_PROPS.PAYMENT_SERVICES]: {
          ...paymentServices,
          [activePaymentType]: {
            ...paymentServices?.[activePaymentType],
            data: {
              ...paymentServices?.[activePaymentType]?.data,
              [activeServiceId]: {
                ...paymentServices?.[activePaymentType]?.data?.[
                  activeServiceId
                ],
                activeNetwork: currentNetwork
              }
            }
          }
        }
      };
    });
  },

  SET_CRYPTO_LIMITS: (limits: CryptoLimits) => {
    setState({ [STORE_PROPS.CRYPTO_LIMITS]: limits });
  },
  SET_IS_SERVICE_ALLOWED: (isAllowed: boolean) => {
    setState({
      [STORE_PROPS.IS_SERVICE_ALLOWED]: { isAllowed, isLoaded: true }
    });
  }
});

export const paymentsActions = actionsCreator(usePaymentsStore);
